import { parse } from 'path-browserify';
import slugify from 'slugify';

import assertUnreachable from '@iconik-io/core/src/assert_unreachable';
import {
  type CreateVersionParams,
  createAsset,
  createVersion,
  updateAsset,
} from '@iconik-io/iconik-api/src/sdk/assets';
import {
  closeFile,
  createFile,
  createFileSet,
  createFormat,
  generateFileKeyframes,
} from '@iconik-io/iconik-api/src/sdk/files';
import {
  httpDeleteAssetsByAssetId,
  httpDeleteAssetsByAssetIdUploads,
  httpDeleteAssetsByAssetIdVersionsByVersionId,
} from '@iconik-io/iconik-api/src/services/assets';
import {
  httpDeleteAssetsByAssetIdFileSetsByFileSetId,
  httpDeleteAssetsByAssetIdFormatsByFormatId,
  httpGetAssetsByAssetIdProxies,
  httpGetSharesStoragesByStorageIdFiles,
  httpPostAssetsByAssetIdFilesByFileIdMediainfo,
  httpPostAssetsByAssetIdFilesByFileIdSubtitles,
  httpPostFilesStoragesByStorageId,
} from '@iconik-io/iconik-api/src/services/files';
import { isNotFoundError } from '@iconik-io/iconik-api/src/utils/is_not_found_error';
import { unwrapResponseBody } from '@iconik-io/iconik-api/src/utils/unwrap_response_body';

import type { CollectionEntry } from '../../collections_service';
import {
  CloseFileError,
  FileExistsError,
  GenerateKeyframesError,
  RegisterSubtitlesError,
} from '../../errors';
import {
  type PostCloseAction,
  UPLOADER_STATUS_PAUSED,
  type UploaderTarget,
} from '../../state';
import {
  unwrapFileUploadKind,
  updateFileUploadKind,
} from '../../state/upload_kind';
import getUploadAssetTitle from '../../utils/asset_title';
import { unwrapAssetId, unwrapFileId } from './context';
import type {
  EntitiesLifecycle,
  EntitiesLifecycleContext,
} from './entities_lifecycle';
import { deleteDuringTeardown, failCancel } from './teardown';

async function assertFileDoesNotExist<Meta, ChunkMeta>(
  context: EntitiesLifecycleContext<Meta, ChunkMeta>,
  params: { directoryPath: string; fileName: string },
): Promise<void> {
  const { add_uuid_to_filenames, id } = context.storageOptions;
  if (add_uuid_to_filenames) {
    return Promise.resolve();
  }
  const { directoryPath, fileName } = params;

  // TODO: RP-2007; Merge these API calls into a single one and perform the check
  // based on Share-ID in the backend.
  // https://linear.app/iconik/issue/RP-2007/merge-apis-for-checking-if-file-exists-in-storage
  const isShareUser = context.storageOptions.isShareUser ?? false;
  const request = isShareUser
    ? httpGetSharesStoragesByStorageIdFiles(id, {
        directory_path: directoryPath,
        name: fileName,
      })
    : httpPostFilesStoragesByStorageId(id, {
        file_name: fileName,
        directory_path: directoryPath,
      });

  return context.apiClient
    .request(request, { abortSignal: context.abortList.newSignal() })
    .then(() => {
      // If we get a success response then the file exists
      throw new FileExistsError(directoryPath, fileName);
    })
    .catch((error) => {
      // This is actually the success path. If we get a 404 then the file does not exist
      if (isNotFoundError(error)) {
        return;
      }
      throw error;
    });
}

async function prepareAsset<Meta, ChunkMeta>(
  context: EntitiesLifecycleContext<Meta, ChunkMeta>,
  destinationCollection: CollectionEntry | undefined,
): Promise<{ assetId: string }> {
  const state = context.getState();
  const parsedPath = parse(state.fileInfo.targetPath);
  const assetTitle = getUploadAssetTitle(state);
  const assetACL = state.options.assetACL;
  const type = ['aep', 'prproj'].includes(parsedPath.ext.toLowerCase())
    ? 'NLE_PROJECT'
    : 'ASSET';

  let assetId;
  const target = state.target;
  switch (target.type) {
    case 'newAsset':
      assetId = target.assetId;
      if (!assetId) {
        const asset = await unwrapResponseBody(
          createAsset({
            client: context.apiClient,
            params: {
              title: assetTitle,
              type,
              collectionId: destinationCollection?.collectionId,
              applyAcls: assetACL,
            },
            abortSignal: context.abortList.newSignal(),
          }),
        );
        assetId = asset.id;
        context.updateState({ target: { type: 'newAsset', assetId } });
        context.emit('assetCreated', { state: context.getState(), asset });
      }
      break;
    case 'newVersion':
      assetId = target.assetId;
      if (!target.versionId) {
        const { copyMetadata, copySegments, sourceVersionId } = target;
        let params: CreateVersionParams = { assetId };
        if (copyMetadata !== undefined) {
          params = { assetId, copyMetadata, copySegments, sourceVersionId };
        }
        const response = await unwrapResponseBody(
          createVersion({
            client: context.apiClient,
            params,
            abortSignal: context.abortList.newSignal(),
          }),
        );
        const latestVersion = response.versions?.at(-1);
        if (!latestVersion) {
          throw new Error('Unable to create version');
        }
        context.updateState({
          target: {
            type: 'newVersion',
            assetId,
            versionId: latestVersion.id,
          },
        });
        context.emit('versionCreated', {
          state: context.getState(),
          version: latestVersion,
        });
      }
      break;
    case 'existingAsset':
      assetId = target.assetId;
      if (target.fillsPlaceholder) {
        await updateAsset({
          client: context.apiClient,
          params: { id: assetId, type: 'ASSET' },
          abortSignal: context.abortList.newSignal(),
        });
      }
      break;
    // no default
  }
  return { assetId };
}

/**
 * Whether the asset already has a proxy. Only the custom-format branch of
 * resolvePostCloseAction needs to know, so it is probed there rather than
 * carried in the state.
 */
async function assetHasProxies<Meta, ChunkMeta>(
  context: EntitiesLifecycleContext<Meta, ChunkMeta>,
  assetId: string,
): Promise<boolean> {
  const response = await context.apiClient
    .request(httpGetAssetsByAssetIdProxies(assetId, { per_page: 1 }), {
      abortSignal: context.abortList.newSignal(),
    })
    .catch((error) => {
      // The endpoint answers 404 for an asset that has no proxies at all,
      // which is the question being asked rather than a failure.
      if (isNotFoundError(error)) {
        return undefined;
      }
      throw error;
    });
  return (response?.body.objects?.length ?? 0) > 0;
}

/**
 * What a closed file needs next, derived from what the upload was for:
 *
 * | target / kind                                          | action    |
 * | ------------------------------------------------------ | --------- |
 * | newAsset, newVersion, existingAsset + fillsPlaceholder | keyframes |
 * | existingAsset + subtitles                              | none      |
 * | existingAsset + custom format, asset has no proxies    | keyframes |
 * | existingAsset + custom format, asset has proxies       | mediainfo |
 */
async function resolvePostCloseAction<Meta, ChunkMeta>(
  context: EntitiesLifecycleContext<Meta, ChunkMeta>,
  assetId: string,
): Promise<PostCloseAction> {
  const state = context.getState();
  const configured = state.options.postCloseAction ?? 'auto';
  if (configured !== 'auto') {
    return configured;
  }

  const target = state.target;
  if (target.type !== 'existingAsset' || target.fillsPlaceholder) {
    return 'keyframes';
  }
  if (unwrapFileUploadKind(state).subtitles) {
    return 'none';
  }
  // A custom format on an asset that has no proxy yet still needs one, and
  // generating keyframes produces it. Otherwise only the media info is missing,
  // without which the format lands under "other formats" in the files panel.
  return (await assetHasProxies(context, assetId)) ? 'mediainfo' : 'keyframes';
}

async function runPostCloseAction<Meta, ChunkMeta>(
  context: EntitiesLifecycleContext<Meta, ChunkMeta>,
  action: PostCloseAction,
  params: { assetId: string; fileId: string },
): Promise<void> {
  const { assetId, fileId } = params;
  switch (action) {
    case 'keyframes':
      await generateFileKeyframes({
        client: context.apiClient,
        params: {
          assetId,
          fileId,
          transcodeOptions: {
            use_storage_transcode_ignore_pattern: true,
            priority: context.getState().options.transcodePriority ?? 5,
          },
        },
        abortSignal: context.abortList.newSignal(),
      }).catch((error) => {
        throw new GenerateKeyframesError(error);
      });
      break;
    case 'mediainfo':
      await context.apiClient
        .request(
          httpPostAssetsByAssetIdFilesByFileIdMediainfo(assetId, fileId, {}),
          { abortSignal: context.abortList.newSignal() },
        )
        .catch((error) => {
          // Without media info the format shows under "other formats" instead
          // of media formats. The file itself is uploaded and closed, and this
          // endpoint is noisy enough that failing the upload over it would cost
          // more than the missing metadata does.
          context.emit('log', {
            level: 'warn',
            message: 'Failed extracting media info for the uploaded file',
            error,
            state: context.getState(),
          });
        });
      break;
    case 'none':
      break;
    default:
      assertUnreachable(action);
  }
}

/**
 * Registers the uploaded file as the asset's subtitles. The upload was asked for
 * subtitles, so a file that never became any is a failed upload, not a warning.
 */
async function registerSubtitles<Meta, ChunkMeta>(
  context: EntitiesLifecycleContext<Meta, ChunkMeta>,
  params: { assetId: string; fileId: string },
): Promise<void> {
  const subtitles = unwrapFileUploadKind(context.getState()).subtitles;
  if (!subtitles) {
    return;
  }
  await context.apiClient
    .request(
      httpPostAssetsByAssetIdFilesByFileIdSubtitles(
        params.assetId,
        params.fileId,
        {
          create_transcription: subtitles.createTranscription,
          delete_old_transcriptions: subtitles.deleteOldTranscriptions,
        },
      ),
      { abortSignal: context.abortList.newSignal() },
    )
    .catch((error) => {
      throw new RegisterSubtitlesError(error);
    });
}

/**
 * Removes the format the upload created on an asset that existed before it.
 * Deleting the file set takes its file entries and the uploaded bytes with it.
 */
async function deleteCreatedFormat<Meta, ChunkMeta>(
  context: EntitiesLifecycleContext<Meta, ChunkMeta>,
  assetId: string,
): Promise<void> {
  const { fileSetId, formatId } = unwrapFileUploadKind(context.getState());

  if (fileSetId) {
    await deleteDuringTeardown(
      context,
      httpDeleteAssetsByAssetIdFileSetsByFileSetId(assetId, fileSetId),
    );
  }
  if (formatId) {
    await deleteDuringTeardown(
      context,
      httpDeleteAssetsByAssetIdFormatsByFormatId(assetId, formatId),
    );
  }
}

/**
 * Puts a placeholder back the way it was found. Whatever stopped the upload
 * usually stops this too, and raising it would only bury that cause and abandon
 * the rest of the teardown, so it is logged instead.
 */
async function revertFilledPlaceholder<Meta, ChunkMeta>(
  context: EntitiesLifecycleContext<Meta, ChunkMeta>,
): Promise<void> {
  const target = context.getState().target;
  if (target.type !== 'existingAsset' || !target.fillsPlaceholder) {
    return;
  }
  await updateAsset({
    client: context.apiClient,
    params: { id: target.assetId, type: 'PLACEHOLDER' },
  }).catch((error) => {
    context.emit('log', {
      level: 'warn',
      message: 'Failed reverting asset back to a placeholder',
      error,
      state: context.getState(),
    });
  });
}

/**
 * Uploading an ORIGINAL file, as a new asset or as a new version of one.
 */
export const fileEntitiesLifecycle: EntitiesLifecycle = {
  async prepareEntities(context) {
    const state = context.getState();
    const parsedPath = parse(state.fileInfo.targetPath);
    const { customFileName, baseCollectionId, basePath, collectionACL } =
      state.options;
    const uploadKind = unwrapFileUploadKind(state);
    const fileName = customFileName ?? parsedPath.base;
    // The file set keeps the name the user sees; the file itself, and therefore
    // the duplicate check, uses the name it is stored under.
    const storedFileName = uploadKind.slugifyFileName
      ? slugify(fileName)
      : fileName;

    const destinationCollection =
      await context.collectionsManager.getOrCreateCollection(
        parsedPath.dir,
        baseCollectionId,
        context.abortList,
        { applyAcls: collectionACL },
      );
    let directoryPath = basePath ?? destinationCollection?.path ?? '';

    if (context.storageOptions.isShareUser) {
      // Within shares, directoryPath should be relative to the share root.
      //
      // For example, if the shared collection is "share-root/" with path "internal-collection/shared-root/"
      // and we upload to "internal-collection/shared-root/subdir/file.txt",
      // then directoryPath should be "subdir/" and will be expanded to
      // "internal-collection/shared-root/subdir/" on the server.
      //
      // TODO: RP-2007; Remove this once API differences are resolved.
      // https://linear.app/iconik/issue/RP-2007/merge-apis-for-checking-if-file-exists-in-storage
      directoryPath = directoryPath.replace(/^[^/]+\/?(.*)/g, '$1');
    }

    await assertFileDoesNotExist(context, {
      directoryPath,
      fileName: storedFileName,
    });

    const { assetId } = await prepareAsset(context, destinationCollection);

    let formatId = unwrapFileUploadKind(context.getState()).formatId;
    if (!formatId) {
      const format = await unwrapResponseBody(
        createFormat({
          client: context.apiClient,
          params: {
            assetId,
            mimeType: state.fileInfo.mimeType,
            formatName: uploadKind.formatName,
            metadata: uploadKind.formatMetadata,
            storageMethods: [context.storageOptions.method],
          },
          abortSignal: context.abortList.newSignal(),
        }),
      );
      formatId = format.id;
      context.updateState(
        updateFileUploadKind(context.getState(), { formatId }),
      );
      context.emit('formatCreated', { state: context.getState(), format });
    }

    let fileSetId = unwrapFileUploadKind(context.getState()).fileSetId;
    if (!fileSetId) {
      const fileSet = await unwrapResponseBody(
        createFileSet({
          client: context.apiClient,
          params: {
            assetId,
            storageId: context.storageOptions.id,
            directoryPath,
            fileName,
            formatId,
          },
          abortSignal: context.abortList.newSignal(),
        }),
      );
      fileSetId = fileSet.id;
      context.updateState(
        updateFileUploadKind(context.getState(), { fileSetId }),
      );
      context.emit('fileSetCreated', { state: context.getState(), fileSet });
    }

    const fileId = unwrapFileUploadKind(context.getState()).fileId;
    if (!fileId) {
      const file = await unwrapResponseBody(
        createFile({
          client: context.apiClient,
          params: {
            assetId,
            directoryPath,
            fileName: storedFileName,
            formatId,
            fileSetId,
            type: 'FILE',
            storageId: context.storageOptions.id,
            fileSize: state.fileInfo.size,
            // contentType is required for S3 uploads
            contentType: state.fileInfo.mimeType,
            checksum: context.getState().checksum,
          },
          abortSignal: context.abortList.newSignal(),
        }),
      );

      context.updateState(
        updateFileUploadKind(context.getState(), { fileId: file.id }),
      );
      context.emit('uploadEntityCreated', {
        state: context.getState(),
        entity: file,
      });
      context.emit('fileCreated', { state: context.getState(), file });
    }
  },

  async finalize(context) {
    const assetId = unwrapAssetId(context);
    const fileId = unwrapFileId(context);
    const postCloseAction = await resolvePostCloseAction(context, assetId);

    await Promise.all([
      closeFile({
        client: context.apiClient,
        params: {
          assetId,
          fileId,
        },
        abortSignal: context.abortList.newSignal(),
      }).catch((error) => {
        throw new CloseFileError(error);
      }),
      runPostCloseAction(context, postCloseAction, { assetId, fileId }),
    ]);

    await registerSubtitles(context, { assetId, fileId });
  },

  async rollbackEntities(context) {
    await revertFilledPlaceholder(context);
  },

  // Teardown runs after the upload has been aborted, which has already aborted
  // every signal in the abort list, so these requests carry no abort signal.
  async cleanupEntities(context) {
    const target = context.getState().target;
    let clearedTarget: UploaderTarget = target;
    switch (target.type) {
      case 'newAsset':
        {
          const assetId = target.assetId;
          if (assetId) {
            await context.apiClient
              .request(httpDeleteAssetsByAssetIdUploads(assetId), {})
              .catch(async (error) => {
                if (isNotFoundError(error)) {
                  context.emit('log', {
                    level: 'debug',
                    message: 'Asset already deleted',
                    state: context.getState(),
                  });
                  return;
                }
                context.emit('log', {
                  level: 'warn',
                  message:
                    'Failed delete asset upload. Will try normal delete.',
                  error,
                  state: context.getState(),
                });
                // httpDeleteAssetsByAssetIdUploads will fail if the upload is older than 24 hours.
                // We can most likely do a normal delete though so try that before failing.
                await context.apiClient.request(
                  httpDeleteAssetsByAssetId(assetId),
                  {},
                );
              })
              .catch(failCancel);
            clearedTarget = { ...target, assetId: undefined };
          }
        }
        break;
      case 'newVersion':
        {
          const versionId = target.versionId;
          if (versionId) {
            await context.apiClient
              .request(
                httpDeleteAssetsByAssetIdVersionsByVersionId(
                  target.assetId,
                  versionId,
                  { hard_delete: true },
                ),
                {},
              )
              .catch(failCancel);
            clearedTarget = { ...target, versionId: undefined };
          }
        }
        break;
      case 'existingAsset':
        // The asset was there before this upload and stays. Only the format the
        // upload added to it is removed.
        await revertFilledPlaceholder(context);
        await deleteCreatedFormat(context, target.assetId);
        break;
      default:
        assertUnreachable(target);
    }
    context.updateState({
      status: UPLOADER_STATUS_PAUSED,
      chunks: undefined,
      meta: undefined,
      target: clearedTarget,
      ...updateFileUploadKind(context.getState(), {
        formatId: undefined,
        fileSetId: undefined,
        fileId: undefined,
      }),
    });
  },
};
